import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from './UserContext';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const WalletProvider = ({ children }) => {
  const { user } = useUser();
  
  // Mock wallet data for bypass
  const mockWallet = {
    id: 'mock-wallet-123',
    user_id: 'mock-user-123',
    unutilized_amount: 1500.00,
    bonus_amount: 250.00,
    total_amount: 1750.00,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const mockTransactions = [
    {
      id: '1',
      user_id: 'mock-user-123',
      transaction_type: 'DEPOSIT',
      wallet_type: 'UNUTILIZED',
      amount: 1000.00,
      balance_after: 1000.00,
      description: 'Deposit via Razorpay',
      status: 'completed',
      created_at: new Date(Date.now() - 86400000).toISOString()
    },
    {
      id: '2',
      user_id: 'mock-user-123', 
      transaction_type: 'REFERRAL_BONUS',
      wallet_type: 'BONUS',
      amount: 100.00,
      balance_after: 1100.00,
      description: 'Referral bonus for inviting friend',
      status: 'completed',
      created_at: new Date(Date.now() - 172800000).toISOString()
    },
    {
      id: '3',
      user_id: 'mock-user-123',
      transaction_type: 'CONTEST_FEE',
      wallet_type: 'UNUTILIZED', 
      amount: -50.00,
      balance_after: 1050.00,
      description: 'Entry fee for Daily Quiz Contest',
      status: 'completed',
      created_at: new Date(Date.now() - 259200000).toISOString()
    },
    {
      id: '4',
      user_id: 'mock-user-123',
      transaction_type: 'PRIZE_MONEY',
      wallet_type: 'UNUTILIZED',
      amount: 500.00,
      balance_after: 1550.00,
      description: 'Prize money for Weekly Contest #1 rank',
      status: 'completed',
      created_at: new Date(Date.now() - 345600000).toISOString()
    }
  ];

  const mockWithdrawals = [
    {
      id: '1',
      user_id: 'mock-user-123',
      amount: 200.00,
      bank_account_number: '1234567890',
      ifsc_code: 'HDFC0001234',
      account_holder_name: 'Test User',
      status: 'completed',
      created_at: new Date(Date.now() - 604800000).toISOString(),
      processed_at: new Date(Date.now() - 518400000).toISOString()
    }
  ];

  const [wallet, setWallet] = useState(mockWallet);
  const [transactions, setTransactions] = useState(mockTransactions);
  const [withdrawals, setWithdrawals] = useState(mockWithdrawals);
  const [loading, setLoading] = useState(false);

  // Bypass real API calls - mock functions for testing
  const fetchWallet = async () => {
    // Wallet data already set in state
  };

  const fetchTransactions = async (limit = 50, offset = 0, transactionType = null) => {
    // Mock function - returns existing mock data
    return {
      transactions: mockTransactions,
      total: mockTransactions.length
    };
  };

  const fetchWithdrawals = async () => {
    // Withdrawals data already set in state
  };

  const createDepositOrder = async (amount) => {
    // Mock deposit order creation
    return { 
      success: true, 
      data: {
        order_id: 'order_mock_123',
        amount: amount * 100,
        currency: 'INR',
        payment_id: 'payment_mock_123'
      }
    };
  };

  const verifyDeposit = async (paymentId, verificationData) => {
    // Mock deposit verification
    // Add mock transaction to simulate successful deposit
    const newTransaction = {
      id: Date.now().toString(),
      user_id: 'mock-user-123',
      transaction_type: 'DEPOSIT',
      wallet_type: 'UNUTILIZED',
      amount: 500.00,
      balance_after: wallet.total_amount + 500,
      description: 'Deposit via Razorpay',
      status: 'completed',
      created_at: new Date().toISOString()
    };
    
    setTransactions(prev => [newTransaction, ...prev]);
    setWallet(prev => ({
      ...prev,
      unutilized_amount: prev.unutilized_amount + 500,
      total_amount: prev.total_amount + 500
    }));
    
    return { success: true, data: { message: 'Deposit successful', amount: 500 } };
  };

  const createWithdrawal = async (withdrawalData) => {
    // Mock withdrawal creation
    const newWithdrawal = {
      id: Date.now().toString(),
      user_id: 'mock-user-123',
      amount: parseFloat(withdrawalData.amount),
      bank_account_number: withdrawalData.bank_account_number,
      ifsc_code: withdrawalData.ifsc_code,
      account_holder_name: withdrawalData.account_holder_name,
      status: 'pending',
      created_at: new Date().toISOString()
    };
    
    setWithdrawals(prev => [newWithdrawal, ...prev]);
    
    return { success: true, data: { withdrawal_id: newWithdrawal.id } };
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const initiatePayment = async (amount, onSuccess, onError) => {
    try {
      setLoading(true);
      
      // Create order
      const orderResult = await createDepositOrder(amount);
      if (!orderResult.success) {
        onError?.(orderResult.error);
        return;
      }

      // Load Razorpay
      const isRazorpayLoaded = await loadRazorpay();
      if (!isRazorpayLoaded) {
        onError?.('Failed to load payment gateway');
        return;
      }

      const options = {
        key: 'rzp_test_9999999999', // Replace with your actual Razorpay key
        amount: orderResult.data.amount,
        currency: orderResult.data.currency,
        name: 'WinQuizz',
        description: 'Add money to wallet',
        order_id: orderResult.data.order_id,
        handler: async (response) => {
          const verificationResult = await verifyDeposit(orderResult.data.payment_id, response);
          if (verificationResult.success) {
            onSuccess?.(verificationResult.data);
          } else {
            onError?.(verificationResult.error);
          }
        },
        prefill: {
          name: user?.full_name || '',
          email: user?.email || '',
          contact: user?.phone || ''
        },
        notes: {
          user_id: user?.id
        },
        theme: {
          color: '#dc2626' // Red theme to match WinQuizz
        },
        modal: {
          ondismiss: () => {
            onError?.('Payment cancelled');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      
    } catch (error) {
      onError?.(error.message || 'Payment initialization failed');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'DEPOSIT': return 'text-green-600 bg-green-100';
      case 'WITHDRAWAL': return 'text-red-600 bg-red-100';
      case 'CONTEST_FEE': return 'text-orange-600 bg-orange-100';
      case 'PRIZE_MONEY': return 'text-green-600 bg-green-100';
      case 'REFERRAL_BONUS': return 'text-blue-600 bg-blue-100';
      case 'REFUND': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'DEPOSIT': return '💰';
      case 'WITHDRAWAL': return '🏦';
      case 'CONTEST_FEE': return '🎯';
      case 'PRIZE_MONEY': return '🏆';
      case 'REFERRAL_BONUS': return '🎁';
      case 'REFUND': return '↩️';
      default: return '💳';
    }
  };

  const value = {
    wallet,
    transactions,
    withdrawals,
    loading,
    fetchWallet,
    fetchTransactions,
    fetchWithdrawals,
    createDepositOrder,
    verifyDeposit,
    createWithdrawal,
    initiatePayment,
    getTransactionTypeColor,
    getTransactionIcon,
    refreshWallet: fetchWallet
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};